import { CommonModule } from '@angular/common'
import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, inject, signal } from '@angular/core'
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router'
import { MessageService } from '@openng/optimus-ui/api'
import { filter } from 'rxjs/operators'
import { NavigationService } from '../../_shared/navigation/navigation.service'

interface NavData {
  name?: string
  url?: string
  icon?: string
  children?: NavData[]
  divider?: boolean
  title?: boolean
  [key: string]: any
}

import { OptimUiSharedModule } from '../../shared/optimus-ui-shared.module';
@Component({
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, RouterLink, RouterLinkActive],
  selector: 'app-sidebar-nav',
  templateUrl: './sidebar-nav.component.html',
  styleUrls: ['./sidebar-nav.component.scss']
})
export class SidebarNavComponent implements OnInit, OnChanges {
  @Input() navItems: NavData[] = []
  @Input() minimized: boolean = false
  @Output() itemClicked = new EventEmitter<void>()

  private readonly navigation = inject(NavigationService)
  private readonly messageService = inject(MessageService)

  readonly favoriteItems = this.navigation.favoriteItems

  expandedKeys = signal<Set<string>>(new Set())
  activeUrl = signal('')
  hoveredItem: NavData | null = null

  private getItemKey(item: NavData): string {
    return item.url || item.name || ''
  }

  trackNavItem(index: number, item: NavData): string {
    return item.url || item.name || `nav-item-${index}`
  }

  constructor(private router: Router) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.activeUrl.set(event.url)
        this.expandActiveParent()
      })
  }

  ngOnInit() {
    this.activeUrl.set(this.router.url)
    this.expandActiveParent()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['minimized']) {
      if (changes['minimized'].currentValue) {
        this.expandedKeys.set(new Set())
      } else {
        this.expandActiveParent()
      }
    }
  }

  expandActiveParent() {
    const chain = this.findAncestorChain(this.navItems, this.activeUrl())
    if (chain) {
      chain.forEach(i => {
        const key = this.getItemKey(i)
        this.expandedKeys.update(s => {
          const next = new Set(s)
          next.add(key)
          return next
        })
      })
    }
  }

  private findAncestorChain(items: NavData[], url: string, ancestors: NavData[] = []): NavData[] | null {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const found = this.findAncestorChain(item.children, url, [...ancestors, item])
        if (found) {
          return found
        }
      } else if (item.url && url.includes(item.url)) {
        return ancestors
      }
    }
    return null
  }

  private findSiblings(item: NavData, items: NavData[] = this.navItems): NavData[] {
    const key = this.getItemKey(item)
    for (const it of items) {
      if (it.children && it.children.some(c => this.getItemKey(c) === key)) {
        return it.children
      }
      if (it.children && it.children.length > 0) {
        const found = this.findSiblings(item, it.children)
        if (found && found !== this.navItems) {
          return found
        }
      }
    }
    return this.navItems
  }

  toggleExpand(item: NavData) {
    const key = this.getItemKey(item)
    this.expandedKeys.update(s => {
      const next = new Set(s)
      if (next.has(key)) {
        next.delete(key)
      } else {
        this.findSiblings(item).forEach(sib => next.delete(this.getItemKey(sib)))
        next.add(key)
      }
      return next
    })
  }

  isExpanded(item: NavData): boolean {
    return this.expandedKeys().has(this.getItemKey(item))
  }

  isActive(url: string): boolean {
    return this.activeUrl().includes(url)
  }

  isFavorite (url: string): boolean {
    return this.navigation.isFavorite(url)
  }

  canPin (item: NavData): boolean {
    return this.navigation.canPin(item)
  }

  togglePin (item: NavData, event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    if (!item.url) {
      return
    }
    const result = this.navigation.toggle(item.url)
    if (result === 'limit') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Preferiti',
        detail: `Puoi pinnare al massimo ${this.navigation.maxFavorites} voci.`
      })
    }
  }

  unpin (url: string, event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    this.navigation.remove(url)
  }
}
